<script lang="ts">
import type { ComponentPublicInstance } from 'vue';
import type { SortDirection } from '#layers/base/public';
import type { CollectionFieldPath, GetCollectionFieldsOptions, GlobalPrimitiveFieldId, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { TableDisplayOptions } from './LayoutTable.vue';
import { CollectionFieldPathSelect, fieldIdToCollectionFieldPath, getCollectionFieldPathLabels,	getCollectionFields,	getCollectionPrimaryKeyFields,	isFieldHidden, sortFieldsBySortKey, toCollectionFieldPathKey, useDataModelDisplayNames } from '#layers/data-model/public';
import { useInitializedDisplayOptionsModel } from '#layers/listing/composables/use-initialized-display-options-model';
import { initialSortState } from '#layers/listing/utils/initial-sort-state';
import { migrateTableDisplayOptions } from './migrate-table-display-options';
import { tableColumnFieldPathOptions } from './table-column-field-path';

interface InitializeLayoutTableOptionsParams {
	options?: TableDisplayOptions | null;
	collection?: ResolvedMonospaceCollection;
	sortDirections: (fieldId: GlobalPrimitiveFieldId) => SortDirection[];
	/** Whether a column may show, see `useOutputGate`. */
	allowsPath: (fieldPath: CollectionFieldPath) => boolean;
}

export function initializeLayoutTableOptions({ collection, options, sortDirections, allowsPath }: InitializeLayoutTableOptionsParams): TableDisplayOptions {
	let fields = collection
		? sortFieldsBySortKey(
				getCollectionFields(collection, {
					includePrimitiveFields: true,
					includeRelationFields: true,
					includeConstrainedFields: false,
				} satisfies GetCollectionFieldsOptions)
					.filter((field) => !isFieldHidden(field) && allowsPath(fieldIdToCollectionFieldPath(field.id))),
			).map((field) => field.id)
		: [];

	if (fields.length === 0) {
		// fall back to at least show the primary keys if no other fields are available
		fields = collection ? getCollectionPrimaryKeyFields(collection).map((field) => field.id) : [];
	}

	const migratedOptions = migrateTableDisplayOptions(options);

	return {
		sorting: collection ? initialSortState(collection, sortDirections) : [],
		...migratedOptions,
		visibleColumnPaths: migratedOptions.visibleColumnPaths
			?? fields.map((fieldId) => fieldIdToCollectionFieldPath(fieldId)),
	};
}

export function getVisibleColumnPathsInDisplayOrder(
	visibleColumnPaths: CollectionFieldPath[],
	columnOrder?: string[],
): CollectionFieldPath[] {
	if (!columnOrder || columnOrder.length === 0) return visibleColumnPaths;

	const pathOrder = new Map(columnOrder.map((columnId, index) => [columnId, index]));

	return visibleColumnPaths
		.map((fieldPath, index) => ({ fieldPath, index }))
		.toSorted((a, b) => {
			const aOrder = pathOrder.get(toCollectionFieldPathKey(a.fieldPath)) ?? Number.POSITIVE_INFINITY;
			const bOrder = pathOrder.get(toCollectionFieldPathKey(b.fieldPath)) ?? Number.POSITIVE_INFINITY;

			return aOrder === bOrder ? a.index - b.index : aOrder - bOrder;
		})
		.map(({ fieldPath }) => fieldPath);
}

// Action columns (e.g. TanStack's `$add-field`) use a `$` prefix and never collide with
// field-path keys, which are joined field IDs.
function isActionColumnId(columnId: string): boolean {
	return columnId.startsWith('$');
}

export function getColumnOrderForVisibleColumnPaths(
	visibleColumnPaths: CollectionFieldPath[],
	previousColumnOrder?: string[],
): string[] {
	const fieldPathKeys = visibleColumnPaths.map((fieldPath) => toCollectionFieldPathKey(fieldPath));

	if (!previousColumnOrder || previousColumnOrder.length === 0) return fieldPathKeys;

	// Preserve action-column entries (e.g. `$add-field`) so rewriting columnOrder from
	// visibleColumnPaths does not erase non-field columns TanStack manages alongside fields.
	const actionColumnEntries = previousColumnOrder.filter((entry) => isActionColumnId(entry));

	return [...fieldPathKeys, ...actionColumnEntries];
}
</script>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useAppConfig } from '#app/config';
import LayoutDisplayOptionSort from '#layers/listing/components/Layout/DisplayOption/LayoutDisplayOptionSort.vue';
import { useOutputGate } from '#layers/listing/composables/use-output-gate';
import { useSortGate } from '#layers/listing/composables/use-sort-gate';
import LayoutTableFieldPathLabel from './LayoutTableFieldPathLabel.vue';

interface VisibleColumnPathItem {
	id: string;
	fieldPath: CollectionFieldPath;
	labels: string[];
}

interface TableDisplayOptionProps {
	collection: ResolvedMonospaceCollection;
	displayOptions?: TableDisplayOptions;
	sharedDisplayOptions?: TableDisplayOptions;
}

const props = defineProps<TableDisplayOptionProps>();
const emit = defineEmits<{
	'update:displayOptions': [value: TableDisplayOptions | undefined];
}>();

const sortGate = useSortGate(() => props.collection);
const outputGate = useOutputGate(() => props.collection);

const modelValue = useInitializedDisplayOptionsModel(
	{
		baselineDisplayOptions: () => props.sharedDisplayOptions,
		displayOptions: () => props.displayOptions,
		initializedBaselineOptions: () => initializeLayoutTableOptions({ options: props.sharedDisplayOptions, collection: props.collection, sortDirections: sortGate.directions, allowsPath: outputGate.allowsPath }),
		initializedOptions: () => initializeLayoutTableOptions({ options: props.displayOptions, collection: props.collection, sortDirections: sortGate.directions, allowsPath: outputGate.allowsPath }),
	},
	(value) => emit('update:displayOptions', value),
);

const appConfig = useAppConfig();
const { getFieldDisplayName } = useDataModelDisplayNames();
const fieldsPopoverOpen = ref(false);
const fieldsPopoverTriggerRef = useTemplateRef<HTMLElement | ComponentPublicInstance>('fieldsPopoverTrigger');

const fieldsPopoverReference = {
	getBoundingClientRect: () => {
		const fieldsPopoverTrigger = getFieldsPopoverTriggerElement();
		const displayOptionsPopoverContent = fieldsPopoverTrigger?.closest('[data-slot="content"]');
		const referenceRect = displayOptionsPopoverContent?.getBoundingClientRect() ?? fieldsPopoverTrigger?.getBoundingClientRect();

		// Zero-sized point anchor at the outer popover's content corner so the stacked
		// popover lines up flush; right/bottom are derived from width/height to stay
		// self-consistent even if the dimensions ever change.
		const width = 0;
		const height = 0;

		if (!referenceRect) {
			return {
				x: 0,
				y: 0,
				width,
				height,
				top: 0,
				right: width,
				bottom: height,
				left: 0,
			};
		}

		return {
			x: referenceRect.left,
			y: referenceRect.top,
			width,
			height,
			top: referenceRect.top,
			right: referenceRect.left + width,
			bottom: referenceRect.top + height,
			left: referenceRect.left,
		};
	},
};

const fieldsPopoverContent = {
	reference: fieldsPopoverReference,
	side: 'bottom',
	align: 'start',
	sideOffset: 8,
	alignOffset: 8,
	avoidCollisions: false,
	collisionPadding: 8,
} as const;

function getFieldsPopoverTriggerElement() {
	const trigger = fieldsPopoverTriggerRef.value;

	if (trigger instanceof HTMLElement) return trigger;

	return trigger?.$el instanceof HTMLElement ? trigger.$el : null;
}

function closeFieldsPopover() {
	fieldsPopoverOpen.value = false;
}

const visibleColumnPaths = computed(() => modelValue.value?.visibleColumnPaths ?? []);
const orderedVisibleColumnPaths = computed(() =>
	getVisibleColumnPathsInDisplayOrder(visibleColumnPaths.value, modelValue.value?.columnOrder),
);

const visibleColumnPathItems = computed<VisibleColumnPathItem[]>({
	get: () => orderedVisibleColumnPaths.value.map((fieldPath) => ({
		id: toCollectionFieldPathKey(fieldPath),
		fieldPath,
		labels: getCollectionFieldPathLabels(props.collection, fieldPath, getFieldDisplayName),
	})),
	set: (items) => {
		updateColumnOrder(items.map((item) => item.fieldPath));
	},
});

const visibleColumnPathKeys = computed(() =>
	new Set(orderedVisibleColumnPaths.value.map((path) => toCollectionFieldPathKey(path))),
);

function updateVisibleColumnPaths(
	visibleColumnPaths: CollectionFieldPath[],
	columnOrderPaths: CollectionFieldPath[] = visibleColumnPaths,
) {
	if (!modelValue.value) return;

	modelValue.value = {
		...modelValue.value,
		visibleColumnPaths,
		columnOrder: getColumnOrderForVisibleColumnPaths(columnOrderPaths, modelValue.value.columnOrder),
	};
}

function updateColumnOrder(visibleColumnPaths: CollectionFieldPath[]) {
	if (!modelValue.value) return;

	modelValue.value = {
		...modelValue.value,
		columnOrder: getColumnOrderForVisibleColumnPaths(visibleColumnPaths, modelValue.value.columnOrder),
	};
}

function addColumnPath(fieldPath: CollectionFieldPath) {
	if (!modelValue.value) return;

	const key = toCollectionFieldPathKey(fieldPath);

	if (visibleColumnPathKeys.value.has(key)) return;

	updateVisibleColumnPaths([...visibleColumnPaths.value, fieldPath], [...orderedVisibleColumnPaths.value, fieldPath]);
}

function removeColumnPath(index: number) {
	if (!modelValue.value) return;

	const fieldPathToRemove = orderedVisibleColumnPaths.value[index];

	if (!fieldPathToRemove) return;

	const keyToRemove = toCollectionFieldPathKey(fieldPathToRemove);

	updateVisibleColumnPaths(
		visibleColumnPaths.value.filter((fieldPath) => toCollectionFieldPathKey(fieldPath) !== keyToRemove),
		orderedVisibleColumnPaths.value.filter((fieldPath) => toCollectionFieldPathKey(fieldPath) !== keyToRemove),
	);
}

function isAddableFieldPath(fieldPath: CollectionFieldPath) {
	return outputGate.allowsPath(fieldPath) && !visibleColumnPathKeys.value.has(toCollectionFieldPathKey(fieldPath));
}
</script>

<template>
	<template v-if="modelValue">
		<LayoutDisplayOptionSort v-model="modelValue.sorting" :collection />

		<DFormField :label="$t('common.fields')">
			<DPopover
				v-model:open="fieldsPopoverOpen"
				:content="fieldsPopoverContent"
				:ui="{ content: 'flex min-w-80 flex-col' }"
			>
				<DButton
					ref="fieldsPopoverTrigger"
					type="button"
					data-testid="visible-fields-summary"
					variant="outline"
					color="neutral"
					size="sm"
					class="w-full justify-between py-2 text-xs font-normal"
					:pressed="fieldsPopoverOpen"
				>
					<span class="flex min-w-0 items-center gap-1 text-left">
						<template v-if="visibleColumnPathItems.length > 0 && visibleColumnPathItems.length <= 2">
							<template
								v-for="(item, index) in visibleColumnPathItems"
								:key="item.id"
							>
								<LayoutTableFieldPathLabel :labels="item.labels" class="min-w-0" />
								<span v-if="index < visibleColumnPathItems.length - 1" class="text-muted">,</span>
							</template>
						</template>
						<span v-else class="truncate">
							{{ $t('common.n_fields', visibleColumnPathItems.length) }}
						</span>
					</span>
				</DButton>

				<template #content>
					<div class="flex h-10 items-center justify-between border-b border-muted px-3">
						<h3 class="text-sm font-medium text-default">
							{{ $t('common.fields') }}
						</h3>
						<DButton
							variant="ghost"
							color="neutral"
							size="xs"
							:icon="appConfig.ui.icons.close"
							:aria-label="$t('common.close')"
							@click="closeFieldsPopover"
						/>
					</div>

					<div class="flex flex-col gap-2 p-2">
						<p
							v-if="visibleColumnPathItems.length === 0"
							data-testid="visible-fields-empty"
							class="px-2 py-3 text-xs text-muted"
						>
							{{ $t('common.n_fields', 0) }}
						</p>

						<DList
							v-else
							v-model:items="visibleColumnPathItems"
							data-testid="visible-fields-list"
							sortable
							allow-drag-only-on-handle
							variant="ghost"
							size="sm"
							:ui="{
								root: 'max-h-72',
								item: {
									base: 'min-h-9 pe-1',
									content: 'ps-4',
									trailing: 'ms-2 flex shrink-0 justify-end',
									handle: 'opacity-100',
								},
							}"
						>
							<template #item-label="{ item }">
								<LayoutTableFieldPathLabel :labels="item.labels" />
							</template>
							<template #item-trailing="{ index }">
								<DButton
									data-testid="remove-visible-field"
									variant="ghost"
									color="neutral"
									size="xs"
									:icon="appConfig.ui.icons.close"
									:aria-label="$t('common.remove')"
									class="text-muted"
									@click.stop="removeColumnPath(index)"
								/>
							</template>
						</DList>

						<CollectionFieldPathSelect
							:collection
							v-bind="tableColumnFieldPathOptions"
							:filter-field-path="isAddableFieldPath"
							:search-placeholder="$t('common.search')"
							size="md"
							@select="addColumnPath"
						>
							<template #default>
								<DButton
									data-testid="add-visible-field"
									variant="ghost"
									color="neutral"
									size="sm"
									:icon="appConfig.ui.icons.plus"
									:label="$t('common.add_field')"
									class="justify-center"
								/>
							</template>
						</CollectionFieldPathSelect>
					</div>
				</template>
			</DPopover>
		</DFormField>
	</template>
</template>
